import { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, message, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getProjectSettings, updateProjectSettings, getProjectTemplates, getPublicCategories, deleteProject } from '@services/projects.service';
import { getClients } from '@services/clients.service';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_SETTINGS_VIEW, PROJECT_SETTINGS_UPDATE, PROJECT_DELETE } from '@/ability';
import { invalidateProjectQueries } from '../projectQueryInvalidation';
import { formatUnderscoreToSpace } from '@/utils/formatText';
import { colors } from '@/utils/colorSystem';

export const PROJECT_SETTINGS_QUERY_KEY = (projectId) => ['projects', projectId, 'settings'];
export const PROJECT_TEMPLATES_QUERY_KEY = ['project-templates'];
export const PUBLIC_CATEGORIES_QUERY_KEY = ['public-categories'];

export const PROJECT_TYPE_OPTIONS = [
    { value: 'fixed_sdlc', label: 'Fixed SDLC' },
    { value: 'website_design', label: 'Website Design' },
    { value: 'hourly_resource_augment', label: 'Hourly Resource Augment' },
    { value: 'fixed_recurring_resource_augment', label: 'Fixed Recurring Resource Augment' },
    { value: 'hourly_project_milestones', label: 'Hourly Project Milestones' },
    { value: 'in_house_internal', label: 'In House Internal' },
];

export const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
];

export const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
];

export const ALL_MODULES_CONFIG = [
    {
        key: 'isQA',
        label: 'QA Testing',
        description: 'Quality assurance submissions and bug tracking',
        iconName: 'BugOutlined',
        color: colors.error?.[500] || '#ef4444',
        bgColor: colors.error?.[50] || '#fef2f2',
    },
    {
        key: 'isPMO',
        label: 'PMO',
        description: 'Project management office oversight and reviews',
        iconName: 'ProjectOutlined',
        color: colors.primary[600],
        bgColor: colors.primary[50],
    },
    {
        key: 'isBlockers',
        label: 'Blockers',
        description: 'Track and resolve project impediments',
        iconName: 'StopOutlined',
        color: colors.warning?.[500] || '#f59e0b',
        bgColor: colors.warning?.[50] || '#fffbeb',
    },
    {
        key: 'isCollaboration',
        label: 'Collaboration',
        description: 'Team discussions and collaborative workspaces',
        iconName: 'TeamOutlined',
        color: colors.info?.[500] || '#3b82f6',
        bgColor: colors.info?.[50] || '#eff6ff',
    },
    {
        key: 'isBacklogs',
        label: 'Backlogs',
        description: 'Manage product and sprint backlogs',
        iconName: 'UnorderedListOutlined',
        color: colors.success?.[500] || '#10b981',
        bgColor: colors.success?.[50] || '#ecfdf5',
    },
    {
        key: 'isChangeRequest',
        label: 'Change Requests',
        description: 'Formal scope and requirement change tracking',
        iconName: 'SwapOutlined',
        color: '#8b5cf6',
        bgColor: '#f5f3ff',
    },
    {
        key: 'isReviews',
        label: 'Reviews',
        description: 'Code reviews and approval workflows',
        iconName: 'StarOutlined',
        color: '#ec4899',
        bgColor: '#fdf2f8',
    },
];

/**
 * Hook for managing project settings.
 */
export function useProjectSettings(projectId) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const [form] = Form.useForm();
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [clientModalOpen, setClientModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [clientSearch, setClientSearch] = useState('');
    const [tempClientId, setTempClientId] = useState(null);
    const [fetchClients, setFetchClients] = useState(false);
    const debounceTimerRef = useRef(null);
    
    const canView = can(PROJECT_SETTINGS_VIEW);
    const canUpdate = can(PROJECT_SETTINGS_UPDATE);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const {
        data: settings = {},
        isLoading: settingsLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: PROJECT_SETTINGS_QUERY_KEY(projectId),
        queryFn: () => getProjectSettings(projectId),
        enabled: !!projectId && canView,
    });

    const templatesQuery = useQuery({
        queryKey: PROJECT_TEMPLATES_QUERY_KEY,
        queryFn: getProjectTemplates,
        staleTime: 5 * 60_000,
        enabled: canUpdate,
    });

    const categoriesQuery = useQuery({
        queryKey: PUBLIC_CATEGORIES_QUERY_KEY,
        queryFn: getPublicCategories,
        staleTime: 10 * 60_000,
        enabled: canUpdate,
    });

    const clientsQuery = useQuery({
        queryKey: ['clients', 'project-settings'],
        queryFn: () => getClients({ limit: 100, type: 'all' }),
        staleTime: 30_000,
        enabled: canUpdate && fetchClients,
    });

    // Normalize: cache may hold { data: [], total } (from useProjectTemplates) or a plain array
    const templatesList = useMemo(() => {
        const raw = templatesQuery.data;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.data)) return raw.data;
        return [];
    }, [templatesQuery.data]);

    const templateOptions = useMemo(() => templatesList.map(t => ({ value: t.id, label: t.name })), [templatesList]);

    const selectedTemplateId = Form.useWatch('projectTemplateId', form);
    const selectedTemplate = useMemo(() => templatesList.find(t => t.id === selectedTemplateId), [templatesList, selectedTemplateId]);

    const filteredProjectTypeOptions = useMemo(() => {
        if (!selectedTemplate || !selectedTemplate.allowedProjectTypes?.length) {
            return PROJECT_TYPE_OPTIONS;
        }

        return selectedTemplate.allowedProjectTypes.map(type => {
            const existing = PROJECT_TYPE_OPTIONS.find(opt => opt.value === type);
            if (existing) return existing;
            const label = formatUnderscoreToSpace(type);
            return { value: type, label };
        });
    }, [selectedTemplate]);

    const categoryOptions = useMemo(() => (categoriesQuery.data ?? []).map(c => ({ value: c.id, label: c.name })), [categoriesQuery.data]);
    const clientOptions = useMemo(() => {
        const list = (clientsQuery.data?.data ?? []).map(c => {
            const name = c?.name ?? '';
            const email = c?.email ?? '';
            const base = name ? (email ? `${name} (${email})` : name) : email || c?.id;
            return { value: c?.id, label: base };
        });

        // In-memory inject current settings client name to avoid showing raw ID before lazy API fires
        if (settings.clientId && settings.client) {
            const exists = list.some(item => item.value === settings.clientId);
            if (!exists) {
                const name = settings.client.name ?? '';
                const email = settings.client.email ?? '';
                const base = name ? (email ? `${name} (${email})` : name) : email || settings.clientId;
                list.unshift({ value: settings.clientId, label: base });
            }
        }

        return list;
    }, [clientsQuery.data, settings.clientId, settings.client]);

    useEffect(() => {
        if (settings && Object.keys(settings).length > 0) {
            form.setFieldsValue({
                name: settings.name,
                slug: settings.slug,
                priority: settings.priority,
                status: settings.status,
                projectType: settings.projectType,
                projectTemplateId: settings.projectTemplateId,
                categoryId: settings.categoryId,
                clientId: settings.clientId,
                budget: settings.budget,
                startDate: settings.startDate ? dayjs(settings.startDate) : null,
                endDate: settings.endDate ? dayjs(settings.endDate) : null,
                milestoneDueDateRule: settings.milestoneDueDateRule,
                sprintDateRule: settings.sprintDateRule || 'relaxing',
                isQA: settings.modules?.isQA?.enabled,
                isPMO: settings.modules?.isPMO?.enabled,
                isBlockers: settings.modules?.isBlockers?.enabled,
                isCollaboration: settings.modules?.isCollaboration?.enabled,
                isBacklogs: settings.modules?.isBacklogs?.enabled,
                isChangeRequest: settings.modules?.isChangeRequest?.enabled,
                isReviews: settings.modules?.isReviews?.enabled,
                isStarred: settings.isStarred,
            });
        }
    }, [settings, form]);

    const updateMutation = useMutation({
        mutationFn: (body) => updateProjectSettings(projectId, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PROJECT_SETTINGS_QUERY_KEY(projectId) });
        },
    });

    const updateSettings = useCallback(
        (body, callbacks = {}) => {
            updateMutation.mutate(body, {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            });
        },
        [updateMutation]
    );

    const handleSave = useCallback((values, immediate = false) => {
        // Build patch body by comparing with original settings
        const patchBody = {};
        
        const compareAndAdd = (key, val) => {
            let original = settings[key];
            if (['isQA', 'isPMO', 'isBlockers', 'isCollaboration', 'isBacklogs', 'isChangeRequest', 'isReviews'].includes(key)) {
                original = settings.modules?.[key]?.enabled;
            }

            // Handle dates
            if (val && (val._isAMomentObject || val instanceof dayjs)) {
                const iso = val.toISOString();
                if (iso !== original) patchBody[key] = iso;
            } else if (val !== original) {
                patchBody[key] = val === undefined ? null : val;
            }
        };

        Object.keys(values).forEach(key => compareAndAdd(key, values[key]));

        if (Object.keys(patchBody).length === 0) {
            return;
        }

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        const runUpdate = () => {
            updateMutation.mutate(patchBody, {
                onSuccess: () => message.success('Project settings updated'),
                onError: (err) => {
                    message.error(err?.response?.data?.message || err?.message || 'Failed to update settings');
                    // Revert the form fields to the last known database values
                    if (settings && Object.keys(settings).length > 0) {
                        form.setFieldsValue({
                            name: settings.name,
                            slug: settings.slug,
                            priority: settings.priority,
                            status: settings.status,
                            projectType: settings.projectType,
                            projectTemplateId: settings.projectTemplateId,
                            categoryId: settings.categoryId,
                            clientId: settings.clientId,
                            budget: settings.budget,
                            startDate: settings.startDate ? dayjs(settings.startDate) : null,
                            endDate: settings.endDate ? dayjs(settings.endDate) : null,
                            milestoneDueDateRule: settings.milestoneDueDateRule,
                            sprintDateRule: settings.sprintDateRule || 'relaxing',
                            isQA: settings.modules?.isQA?.enabled,
                            isPMO: settings.modules?.isPMO?.enabled,
                            isBlockers: settings.modules?.isBlockers?.enabled,
                            isCollaboration: settings.modules?.isCollaboration?.enabled,
                            isBacklogs: settings.modules?.isBacklogs?.enabled,
                            isChangeRequest: settings.modules?.isChangeRequest?.enabled,
                            isReviews: settings.modules?.isReviews?.enabled,
                            isStarred: settings.isStarred,
                        });
                    }
                }
            });
        };

        if (immediate) {
            runUpdate();
        } else {
            debounceTimerRef.current = setTimeout(runUpdate, 800);
        }
    }, [settings, updateMutation]);

    const navigate = useNavigate();
    const canDelete = can(PROJECT_DELETE);

    const deleteMutation = useMutation({
        mutationFn: () => deleteProject(projectId),
        onSuccess: () => {
            message.success('Project deleted successfully');
            setDeleteModalOpen(false);
            // Remove deleted project queries from the cache to prevent any refetching
            queryClient.removeQueries({ queryKey: ['projects', 'detail', projectId] });
            queryClient.removeQueries({ queryKey: ['projects', projectId] });
            
            // Invalidate only the project list queries
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey;
                    return (
                        Array.isArray(key) &&
                        key[0] === 'projects' &&
                        key[1] !== 'detail' &&
                        key[1] !== projectId
                    );
                }
            });
            navigate('/tenant/projects');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err?.message || 'Failed to delete project');
        }
    });

    const handleTemplateModalOpen = () => {
        Modal.confirm({
            title: 'Change Project Template?',
            content: 'Are you sure you want to change the template for this project? This will adjust active modules and dates rules.',
            okText: 'Yes',
            cancelText: 'No',
            centered: true,
            onOk: () => {
                setTemplateModalOpen(true);
            }
        });
    };

    const handleDeleteProject = () => {
        setDeleteModalOpen(true);
    };

    const handleClientModalOpen = () => {
        setTempClientId(form.getFieldValue('clientId'));
        setClientSearch('');
        Modal.confirm({
            title: 'Change Project Client?',
            content: 'Are you sure you want to change the client for this project?',
            okText: 'Yes',
            cancelText: 'No',
            centered: true,
            onOk: () => {
                setClientModalOpen(true);
                setFetchClients(true);
            }
        });
    };

    const handleClientModalSave = () => {
        form.setFieldsValue({ clientId: tempClientId });
        setClientModalOpen(false);
        const currentValues = form.getFieldsValue();
        handleSave({
            ...currentValues,
            clientId: tempClientId
        }, true);
    };

    const clientsList = clientsQuery.data?.data ?? [];
    const filteredClients = useMemo(() => {
        const query = clientSearch.trim().toLowerCase();
        if (!query) return clientsList;
        return clientsList.filter(c => 
            String(c.name ?? '').toLowerCase().includes(query) || 
            String(c.email ?? '').toLowerCase().includes(query)
        );
    }, [clientsList, clientSearch]);

    const activeClientId = Form.useWatch('clientId', form);
    const activeClient = useMemo(() => {
        return clientsList.find(c => c.id === activeClientId) || ((!settings.clientId && !activeClientId) || settings.clientId === activeClientId ? settings.client : null);
    }, [clientsList, activeClientId, settings.clientId, settings.client]);

    const getClientTypeBadge = useCallback((client) => {
        const type = client?.clientType || client?.client_type || '';
        const str = String(type).trim().toLowerCase();
        
        if (str === 'out of the platform' || str === 'out_of_platform') {
            return {
                label: 'Out of Platform',
                bg: '#fffbeb',
                color: '#b45309'
            };
        }
        if (str === 'my_client' || str === 'my client') {
            return {
                label: 'My Client',
                bg: '#f0fdf4',
                color: '#15803d'
            };
        }
        if (str === 'sprinto_client' || str === 'sprinto client') {
            return {
                label: 'Sprinto Client',
                bg: '#eff6ff',
                color: '#1d4ed8'
            };
        }
        if (!type) return null;
        return {
            label: str.replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
            bg: colors.neutral[100],
            color: colors.neutral[600]
        };
    }, []);

    const activeClientTypeStyle = useMemo(() => {
        const type = activeClient?.clientType || activeClient?.client_type || (activeClientId === settings?.clientId || !activeClientId ? (settings?.clientType || settings?.client_type) : '');
        return getClientTypeBadge({ clientType: type });
    }, [activeClient, activeClientId, settings?.clientId, settings?.clientType, settings?.client_type, getClientTypeBadge]);

    return {
        canView,
        canUpdate,
        settings,
        isLoading: settingsLoading,
        isError,
        refetch,
        optionsLoading: templatesQuery.isLoading || categoriesQuery.isLoading,
        clients: clientsList,
        clientsLoading: clientsQuery.isLoading,
        onClientsDropdownOpen: () => setFetchClients(true),
        form,
        templateModalOpen,
        setTemplateModalOpen,
        clientModalOpen,
        setClientModalOpen,
        clientSearch,
        setClientSearch,
        tempClientId,
        setTempClientId,
        handleClientModalOpen,
        handleClientModalSave,
        handleTemplateModalOpen,
        handleDeleteProject,
        filteredClients,
        activeClient,
        activeClientTypeStyle,
        getClientTypeBadge,
        templates: templatesQuery.data ?? [],
        templatesLoading: templatesQuery.isLoading,
        projectTypeOptions: filteredProjectTypeOptions,
        templateOptions,
        categoryOptions,
        clientOptions,
        updatePending: updateMutation.isPending,
        handleTemplateSelect: (template) => {
            const nextId = template?.id || null;
            form.setFieldsValue({ 
                projectTemplateId: nextId,
                projectType: undefined // Reset project type when template changes to force re-selection
            });
            setTemplateModalOpen(false);
            
            // Immediately save template change
            const currentValues = form.getFieldsValue();
            handleSave({
                ...currentValues,
                projectTemplateId: nextId,
                projectType: null
            }, true);
        },
        handleSave,
        canDelete,
        deleteModalOpen,
        setDeleteModalOpen,
        deleteProject: deleteMutation.mutate,
        isDeleting: deleteMutation.isPending
    };
}
